import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CameraAlt as CameraIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  //   Pending as PendingIcon, // Unused import
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { useTranslation } from 'react-i18next';

const BecomeDriverPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUserData } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Returning Driver State
  const [showReturningModal, setShowReturningModal] = useState(false);

  const steps = [
    t('becomeDriver.welcome', 'Instructions'),
    t('becomeDriver.steps.licenseFront', 'License Front'),
    t('becomeDriver.steps.licenseBack', 'License Back'),
    t('becomeDriver.takeSelfieTitle', 'Take Selfie'),
    t('becomeDriver.verifyingTitle', 'Verification'),
    t('becomeDriver.successTitle', 'Complete')
  ];

  // Get return URL from location state
  const returnUrl = (location.state as any)?.returnUrl || null;

  // Files
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [licensePreview, setLicensePreview] = useState<string>('');
  const [licenseBackFile, setLicenseBackFile] = useState<File | null>(null);
  const [licenseBackPreview, setLicenseBackPreview] = useState<string>('');
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string>('');

  // Verification results
  const [verificationResult, setVerificationResult] = useState<any>(null);

  // Refs
  const licenseInputRef = useRef<HTMLInputElement>(null);
  const licenseBackInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  // Synchronize video stream once camera is active and DOM element is rendered
  useEffect(() => {
    if (cameraActive && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraActive, cameraStream]);

  // Clean up camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Check current driver status - redirect if already verified (APPROVED or AI_VERIFIED)
  useEffect(() => {
    if (user?.driverStatus === 'APPROVED' || user?.driverStatus === 'AI_VERIFIED') {
      // If there's a return URL, go back there; otherwise go to create-ride
      if (returnUrl) {
        navigate(returnUrl, { replace: true });
      } else {
        navigate('/create-ride');
      }
    }
  }, [user, navigate, returnUrl]);

  const handleLicenseUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(t('becomeDriver.errors.licenseSize', 'License image must be less than 5MB'));
        return;
      }
      setLicenseFile(file);
      if (file.type === 'application/pdf') {
        setLicensePreview('pdf');
      } else {
        setLicensePreview(URL.createObjectURL(file));
      }
      setError('');
    }
  };

  const handleLicenseBackUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(t('becomeDriver.errors.licenseSize', 'License image must be less than 5MB'));
        return;
      }
      setLicenseBackFile(file);
      if (file.type === 'application/pdf') {
        setLicenseBackPreview('pdf');
      } else {
        setLicenseBackPreview(URL.createObjectURL(file));
      }
      setError('');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      setCameraStream(stream);
      setCameraActive(true);
    } catch (err) {
      setError(t('becomeDriver.errors.cameraAccessEnforced', 'Unable to access camera. Please ensure camera permissions are granted and that you have a working camera. A live selfie is required for security verification.'));
      console.error('Camera error:', err);
    }
  };

  const captureSelfie = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);

        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
            setSelfieFile(file);
            setSelfiePreview(URL.createObjectURL(file));
            stopCamera();
          }
        }, 'image/jpeg', 0.95);
      }
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraActive(false);
  };

  const handleCloseReturningModal = async () => {
    setShowReturningModal(false);
    setActiveStep(5);
    await refreshUserData();
    const targetUrl = returnUrl && returnUrl !== '/create-ride' ? returnUrl : '/vehicles';
    const targetState = targetUrl === '/vehicles' ? { showAddVehicleModal: true } : undefined;
    setTimeout(() => {
      navigate(targetUrl, { replace: true, state: targetState });
    }, 2000);
  };

  const handleSubmit = async () => {
    if (!licenseFile || !licenseBackFile || !selfieFile) {
      setError(t('becomeDriver.errors.allRequired', 'Please upload license front, license back, and selfie'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('license', licenseFile);
      formData.append('licenseBack', licenseBackFile);
      formData.append('selfie', selfieFile);

      const response = await apiService.postFormData('/driver/upload-license', formData);

      if (response.success) {
        setVerificationResult(response.data);
        setSuccess(response.message || t('becomeDriver.successTitle', 'Verification successful'));

        if ((response as any).isReturningDriver) {
          setShowReturningModal(true);
          return;
        }

        setShowReturningModal(false);
        setActiveStep(5);

        // Refresh user data to get updated driverStatus
        await refreshUserData();

        // Redirect to vehicles/returnUrl on successful document submission
        const targetUrl = returnUrl && returnUrl !== '/create-ride' ? returnUrl : '/vehicles';
        const targetState = targetUrl === '/vehicles' ? { showAddVehicleModal: true } : undefined;
        setTimeout(() => {
          navigate(targetUrl, { replace: true, state: targetState });
        }, 2000); // Give user time to see success message
      } else {
        setError(response.message || t('becomeDriver.failedTitle', 'Verification failed'));
        setVerificationResult(response);
        setActiveStep(5);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('becomeDriver.errors.submitFailed', 'Failed to submit verification'));
      console.error('Verification error:', err);
      setActiveStep(5);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setError('');

    if (activeStep === 1 && !licenseFile) {
      setError(t('becomeDriver.errors.uploadLicense', 'Please upload your driving license'));
      return;
    }

    if (activeStep === 2 && !licenseBackFile) {
      setError(t('becomeDriver.errors.uploadLicenseBack', 'Please upload the back of your driving license'));
      return;
    }

    if (activeStep === 3 && !selfieFile) {
      setError(t('becomeDriver.errors.uploadSelfie', 'Please take or upload a selfie'));
      return;
    }

    if (activeStep === 3) {
      // Moving to verification step
      setActiveStep(4);
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
              {t('becomeDriver.welcome', 'Welcome to Driver Verification')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.5rem' }}>
              {t('becomeDriver.description', 'To become a driver on Ride Club, you need to verify your identity. This process includes:')}
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" sx={{ fontSize: '1.5rem' }} /></ListItemIcon>
                <ListItemText primary={t('becomeDriver.steps.licenseFront', 'Upload front side of driving license')} sx={{ '& .MuiListItemText-primary': { fontSize: '1.5rem' } }} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" sx={{ fontSize: '1.5rem' }} /></ListItemIcon>
                <ListItemText primary={t('becomeDriver.steps.licenseBack', 'Upload back side of driving license')} sx={{ '& .MuiListItemText-primary': { fontSize: '1.5rem' } }} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" sx={{ fontSize: '1.5rem' }} /></ListItemIcon>
                <ListItemText primary={t('becomeDriver.steps.selfie', 'Take a selfie to verify your identity')} sx={{ '& .MuiListItemText-primary': { fontSize: '1.5rem' } }} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" sx={{ fontSize: '1.5rem' }} /></ListItemIcon>
                <ListItemText primary={t('becomeDriver.steps.aiVerification', 'AI-powered verification (instant)')} sx={{ '& .MuiListItemText-primary': { fontSize: '1.5rem' } }} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" sx={{ fontSize: '1.5rem' }} /></ListItemIcon>
                <ListItemText primary={t('becomeDriver.steps.adminReview', 'Admin review for final approval')} sx={{ '& .MuiListItemText-primary': { fontSize: '1.5rem' } }} />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
              {t('becomeDriver.selfieDisclaimer', "Your selfie will not be stored. It's only used for face matching verification.")}
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
              {t('becomeDriver.uploadLicenseTitle', 'Upload Your Driving License')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '1.3125rem' }}>
              {t('becomeDriver.uploadLicenseDesc', 'Please upload a clear photo or PDF of the front of your driving license.')}
            </Typography>

            <input
              ref={licenseInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleLicenseUpload}
              style={{ display: 'none' }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {licensePreview ? (
                <Card sx={{ maxWidth: 400, width: '100%', p: 2 }}>
                  {licensePreview === 'pdf' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 1 }}>
                      <Typography sx={{ fontSize: 64 }}>📄</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.3125rem', textAlign: 'center', wordBreak: 'break-all' }}>
                        {licenseFile?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                        PDF Document ({licenseFile ? (licenseFile.size / (1024 * 1024)).toFixed(2) : 0} MB)
                      </Typography>
                    </Box>
                  ) : (
                    <img
                      src={licensePreview}
                      alt="License preview"
                      style={{ width: '100%', height: 'auto' }}
                    />
                  )}
                </Card>
              ) : (
                <Paper
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 250,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => licenseInputRef.current?.click()}
                >
                  <UploadIcon sx={{ fontSize: 96, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.5rem' }}>
                    {t('becomeDriver.clickToUpload', 'Click to upload license front')}
                  </Typography>
                </Paper>
              )}

              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => licenseInputRef.current?.click()}
                sx={{ fontSize: '1.5rem' }}
              >
                {licensePreview ? t('becomeDriver.changeLicense', 'Change License File') : t('becomeDriver.uploadLicenseButton', 'Upload License Front')}
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
              {t('becomeDriver.uploadLicenseBackTitle', 'Upload the Back of your License')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '1.3125rem' }}>
              {t('becomeDriver.uploadLicenseBackSubtitle', 'Please upload a clear photo or PDF of the back side of your driving license showing the barcode.')}
            </Typography>

            <input
              ref={licenseBackInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleLicenseBackUpload}
              style={{ display: 'none' }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {licenseBackPreview ? (
                <Card sx={{ maxWidth: 400, width: '100%', p: 2 }}>
                  {licenseBackPreview === 'pdf' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 1 }}>
                      <Typography sx={{ fontSize: 64 }}>📄</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.3125rem', textAlign: 'center', wordBreak: 'break-all' }}>
                        {licenseBackFile?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                        PDF Document ({licenseBackFile ? (licenseBackFile.size / (1024 * 1024)).toFixed(2) : 0} MB)
                      </Typography>
                    </Box>
                  ) : (
                    <img
                      src={licenseBackPreview}
                      alt="License Back preview"
                      style={{ width: '100%', height: 'auto' }}
                    />
                  )}
                </Card>
              ) : (
                <Paper
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 250,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => licenseBackInputRef.current?.click()}
                >
                  <UploadIcon sx={{ fontSize: 96, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.5rem' }}>
                    {t('becomeDriver.clickToUploadBack', 'Click to upload license back')}
                  </Typography>
                </Paper>
              )}

              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => licenseBackInputRef.current?.click()}
                sx={{ fontSize: '1.5rem' }}
              >
                {licenseBackPreview ? t('becomeDriver.changeLicenseBack', 'Change Back File') : t('becomeDriver.uploadLicenseBackButton', 'Upload License Back')}
              </Button>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
              {t('becomeDriver.takeSelfieTitle', 'Take a Selfie')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '1.3125rem' }}>
              {t('becomeDriver.takeSelfieDesc', 'Please take a live selfie of yourself to complete verification. Photo uploads from your gallery are blocked for safety.')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {selfiePreview ? (
                /* ── Captured preview with oval crop hint ── */
                <Box sx={{ position: 'relative', width: '100%', maxWidth: 400 }}>
                  <Card>
                    <img
                      src={selfiePreview}
                      alt="Selfie preview"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </Card>
                  {/* Oval crop overlay on preview */}
                  <Box sx={{
                    position: 'absolute', inset: 0, pointerEvents: 'none'
                  }}>
                    <svg width="100%" height="100%" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" style={{ opacity: 0.5, display: 'block' }}>
                      <ellipse cx="200" cy="150" rx="110" ry="120"
                        fill="none" stroke="#4CAF50" strokeWidth="3" strokeDasharray="8 4" />
                    </svg>
                  </Box>
                </Box>
              ) : cameraActive ? (
                /* ── Live camera with oval overlay ── */
                <Box sx={{ position: 'relative', width: '100%', maxWidth: 400 }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{ width: '100%', borderRadius: 12, display: 'block' }}
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />

                  {/* Dark vignette + oval cut-out */}
                  <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    <svg
                      width="100%" height="100%"
                      viewBox="0 0 400 300"
                      preserveAspectRatio="xMidYMid slice"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <mask id="ovalMask">
                          <rect width="400" height="300" fill="white" />
                          <ellipse cx="200" cy="150" rx="110" ry="120" fill="black" />
                        </mask>
                      </defs>
                      {/* Dark overlay with oval cutout */}
                      <rect width="400" height="300" fill="rgba(0,0,0,0.45)" mask="url(#ovalMask)" />
                      {/* Oval border */}
                      <ellipse cx="200" cy="150" rx="110" ry="120"
                        fill="none" stroke="white" strokeWidth="3"
                        style={{
                          animation: 'ovalPulse 2s ease-in-out infinite'
                        }}
                      />
                      {/* Corner tick marks */}
                      <line x1="200" y1="20" x2="200" y2="40" stroke="white" strokeWidth="3" strokeLinecap="round" />
                      <line x1="200" y1="260" x2="200" y2="280" stroke="white" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </Box>

                  {/* Instruction label */}
                  <Box sx={{
                    position: 'absolute', bottom: 12, left: 0, right: 0,
                    display: 'flex', justifyContent: 'center', pointerEvents: 'none'
                  }}>
                    <Box sx={{
                      bgcolor: 'rgba(0,0,0,0.6)', color: 'white',
                      px: 2, py: 0.5, borderRadius: 4,
                      fontSize: '0.85rem', fontWeight: 600
                    }}>
                      {t('becomeDriver.faceGuide', 'Center your face inside the oval')}
                    </Box>
                  </Box>

                  {/* CSS animation injected inline */}
                  <style>{`
                    @keyframes ovalPulse {
                      0%, 100% { stroke: white; stroke-width: 3; }
                      50% { stroke: #90CAF9; stroke-width: 4; }
                    }
                  `}</style>
                </Box>
              ) : (
                /* ── Placeholder with oval guide illustration ── */
                <Paper
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 340,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                    position: 'relative',
                    bgcolor: 'grey.50',
                  }}
                >
                  {/* Oval face guide illustration */}
                  <svg width="180" height="220" viewBox="0 0 180 220" style={{ marginBottom: 8 }}>
                    <defs>
                      <mask id="guideMask">
                        <rect width="180" height="220" fill="white" />
                        <ellipse cx="90" cy="105" rx="50" ry="75" fill="black" />
                      </mask>
                    </defs>
                    <rect width="180" height="220" fill="#E3E8EF" mask="url(#guideMask)" rx="12" />
                    {/* Oval border dashed */}
                    <ellipse cx="90" cy="105" rx="50" ry="75"
                      fill="none" stroke="#1976D2" strokeWidth="2.5" strokeDasharray="10 5" />
                    {/* Simple face silhouette */}
                    <ellipse cx="90" cy="95" rx="38" ry="46" fill="#BDBDBD" opacity="0.5" />
                    <ellipse cx="90" cy="175" rx="55" ry="30" fill="#BDBDBD" opacity="0.3" />
                    {/* Eyes */}
                    <ellipse cx="73" cy="85" rx="6" ry="7" fill="#9E9E9E" />
                    <ellipse cx="107" cy="85" rx="6" ry="7" fill="#9E9E9E" />
                    {/* Smile */}
                    <path d="M 75 108 Q 90 120 105 108" stroke="#9E9E9E" strokeWidth="2.5"
                      fill="none" strokeLinecap="round" />
                    {/* Arrow hint */}
                    <text x="90" y="215" textAnchor="middle" fontSize="11"
                      fill="#1976D2" fontWeight="600">
                      Fit your face here
                    </text>
                  </svg>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <CameraIcon sx={{ fontSize: 22 }} />
                    <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                      {t('becomeDriver.useCameraLive', 'Use webcam or camera')}
                    </Typography>
                  </Box>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                {cameraActive ? (
                  <>
                    <Button
                      variant="contained"
                      onClick={captureSelfie}
                      sx={{ fontSize: '1.5rem' }}
                    >
                      {t('becomeDriver.capturePhoto', 'Capture Photo')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={stopCamera}
                      sx={{ fontSize: '1.5rem' }}
                    >
                      {t('becomeDriver.cancelCamera', 'Cancel')}
                    </Button>
                  </>
                ) : !selfiePreview ? (
                  <Button
                    variant="contained"
                    startIcon={<CameraIcon />}
                    onClick={startCamera}
                    sx={{ fontSize: '1.5rem' }}
                  >
                    {t('becomeDriver.useCamera', 'Open Camera')}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSelfieFile(null);
                      setSelfiePreview('');
                    }}
                    sx={{ fontSize: '1.5rem' }}
                  >
                    {t('becomeDriver.retakeSelfie', 'Retake Selfie')}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        );


      case 4:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={90} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
              {t('becomeDriver.verifyingTitle', 'Verifying Your Identity...')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3125rem' }}>
              {t('becomeDriver.verifyingDesc', 'Please wait while we verify your documents')}
            </Typography>
            <LinearProgress sx={{ mt: 3, maxWidth: 400, mx: 'auto', height: 8 }} />
          </Box>
        );

      case 5:
        return (
          <Box>
            {verificationResult && verificationResult.driverStatus ? (
              <>
                <Alert 
                  severity={verificationResult.driverStatus === 'AI_VERIFIED' ? 'success' : 'info'} 
                  sx={{ mb: 3, '& .MuiAlert-message': { fontSize: '1.5rem' } }}
                >
                  <Typography variant="h6" sx={{ fontSize: '1.875rem' }}>
                    {verificationResult.driverStatus === 'AI_VERIFIED' 
                      ? t('becomeDriver.successTitle', 'Verification Successful!') 
                      : t('becomeDriver.pendingTitle', 'Application Submitted!')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '1.3125rem' }}>
                    {success}
                  </Typography>
                </Alert>

                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1.5rem' }}>
                      {t('becomeDriver.faceMatchScore', 'Face Match Score:')} <strong>{verificationResult.faceMatchScore}%</strong>
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={verificationResult.faceMatchScore}
                      sx={{ height: 15, borderRadius: 5, mb: 2 }}
                    />

                    {verificationResult.licenseData && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '1.3125rem' }}>
                          {t('becomeDriver.extractedInfo', 'Extracted License Information:')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '1.3125rem' }}>
                          {t('becomeDriver.name', 'Name:')} {verificationResult.licenseData.preferredName || verificationResult.licenseData.firstName} {verificationResult.licenseData.lastName ? verificationResult.licenseData.lastName.charAt(0) + '.' : ''}
                        </Typography>
                        {verificationResult.licenseData.licenseNumber && (
                          <Typography variant="body2" sx={{ fontSize: '1.3125rem' }}>
                            {t('becomeDriver.licenseNumber', 'License #:')} {verificationResult.licenseData.licenseNumber}
                          </Typography>
                        )}
                        {verificationResult.licenseData.expiryDate && (
                          <Typography variant="body2" sx={{ fontSize: '1.3125rem' }}>
                            {t('becomeDriver.expiry', 'Expiry:')} {new Date(verificationResult.licenseData.expiryDate).toLocaleDateString(i18n.language)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {verificationResult.driverStatus === 'AI_VERIFIED' ? (
                  <Alert severity="success" sx={{ '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
                    <Typography variant="body2" sx={{ fontSize: '1.3125rem' }}>
                      <strong>{t('becomeDriver.autoVerified', 'Congratulations! You have been automatically verified and can now offer rides immediately.')}</strong>
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
                    <Typography variant="body2" sx={{ fontSize: '1.3125rem' }}>
                      <strong>{t('becomeDriver.pendingReview', "Next Step: Your application is pending admin review. You'll be notified once approved.")}</strong>
                    </Typography>
                  </Alert>
                )}

                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                  {returnUrl ? (
                    <Button
                      variant="contained"
                      onClick={() => navigate(returnUrl)}
                      sx={{ fontSize: '1.5rem' }}
                    >
                      {t('becomeDriver.returnButton', 'Return to Previous Page')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/dashboard')}
                        sx={{ fontSize: '1.5rem' }}
                      >
                        {t('becomeDriver.dashboardButton', 'Go to Dashboard')}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/my-rides')}
                        sx={{ fontSize: '1.5rem' }}
                      >
                        {t('becomeDriver.myRidesButton', 'View My Rides')}
                      </Button>
                    </>
                  )}
                </Box>
              </>
            ) : (
              <Alert severity="error" sx={{ '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
                <Typography variant="h6" sx={{ fontSize: '1.875rem' }}>{t('becomeDriver.failedTitle', 'Verification Failed')}</Typography>
                <Typography variant="body2" sx={{ fontSize: '1.3125rem' }}>
                  {error || t('becomeDriver.errors.verificationFailed', 'Unable to verify your identity. Please try again.')}
                </Typography>
                {verificationResult?.reason && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 1, borderLeft: '4px solid', borderColor: 'error.main', textAlign: 'left' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '1.3125rem', color: 'error.main' }}>
                      {t('becomeDriver.aiReason', 'Detailed AI Assessment:')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '1.2rem', fontStyle: 'italic', color: 'text.primary' }}>
                      {verificationResult.reason}
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  color="error"
                  sx={{ mt: 2, fontSize: '1.5rem' }}
                  onClick={() => {
                    setActiveStep(1);
                    setLicenseFile(null);
                    setLicensePreview('');
                    setLicenseBackFile(null);
                    setLicenseBackPreview('');
                    setSelfieFile(null);
                    setSelfiePreview('');
                    setVerificationResult(null);
                  }}
                >
                  {t('becomeDriver.tryAgain', 'Try Again')}
                </Button>
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ fontSize: '3.1875rem' }}>
          {t('becomeDriver.pageTitle', 'Become a Driver')}
        </Typography>

        <Stepper activeStep={activeStep} sx={{ my: 4, '& .MuiStepLabel-label': { fontSize: '1.5rem' } }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, '& .MuiAlert-message': { fontSize: '1.5rem' } }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Dialog
          open={showReturningModal}
          onClose={handleCloseReturningModal}
          PaperProps={{ sx: { p: 2, borderRadius: 3, maxWidth: 500 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <ErrorIcon sx={{ fontSize: 56, color: 'warning.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('becomeDriver.returningDriverTitle', 'Returning Driver Detected')}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
              {t('becomeDriver.returningDriverDesc', 'We found your information in our database. It looks like you have been with us before. You are not eligible for the first-time driver free credits.')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 2, alignItems: 'stretch', px: 3, pb: 3 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleCloseReturningModal}
              sx={{ py: 1.5 }}
            >
              {t('common.continue', 'Continue')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setShowReturningModal(false);
                navigate('/contact-us'); // Or whatever the support route is
              }}
              sx={{ py: 1.5, ml: '0 !important' }}
            >
              {t('becomeDriver.contactAdmin', 'Send Ticket to Admin')}
            </Button>
          </DialogActions>
        </Dialog>

        {renderStepContent()}

        {activeStep < 4 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ fontSize: '1.5rem' }}
            >
              {t('becomeDriver.back', 'Back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              sx={{ fontSize: '1.5rem' }}
            >
              {activeStep === 3 ? t('becomeDriver.submit', 'Submit') : t('becomeDriver.next', 'Next')}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default BecomeDriverPage;

